import { motion } from "framer-motion";
import heroVideo from "../assests/Speechai.mp4";

export default function AudioCollection() {
  return (
    <div className="bg-gray-50 text-gray-900">
      {/* Hero Section */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-6">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-4 tracking-tight font-orbitron"
          >
            🎙️ Powering AI with High-Quality Audio Data
          </motion.h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-raleway">
            We collect, curate, and annotate diverse audio datasets — from human
            speech to environmental sounds — to train smarter, more accurate AI
            systems.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 tracking-tight font-orbitron">
          Why Audio Data Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          Audio is at the heart of communication, recognition, and interaction.
          Our audio collection empowers AI models in:
        </p>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left text-base text-gray-700 leading-relaxed font-raleway">
          <li>🗣️ Speech Recognition Systems</li>
          <li>🌍 Multilingual Voice Assistants</li>
          <li>🎧 Emotion & Sentiment Analysis</li>
          <li>🚗 Autonomous Driving (sound-based alerts)</li>
          <li>🔊 Environmental Sound Classification</li>
          <li>📞 Telecom & Call Center Analytics</li>
        </ul>
      </section>

      {/* Collections */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Our Collections
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto font-raleway">
          {[
            {
              title: "🎤 Speech Datasets",
              desc: "Multiple languages, accents, and dialects with natural conversations.",
            },
            {
              title: "👥 Speaker Identification",
              desc: "Clean, labeled voice samples for biometric verification.",
            },
            {
              title: "🌎 Multilingual Audio",
              desc: "Over 50+ languages for global AI applications.",
            },
            {
              title: "🎶 Environmental Sounds",
              desc: "Traffic, machinery, nature, indoor/outdoor ambient recordings.",
            },
            {
              title: "🧑‍⚕️ Domain-Specific Audio",
              desc: "Healthcare, customer support, education, and more.",
            },
            {
              title: "📝 Annotation Services",
              desc: "Transcriptions, speaker tags, labeling, and emotion classification.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="text-xl font-semibold mb-3 font-orbitron">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          How We Collect & Deliver
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center font-raleway">
          {[
            "Define project requirements",
            "Recruit diverse contributors",
            "Record & capture in real-world settings",
            "Annotate (speaker tags, transcription, labeling)",
            "Quality check & dataset delivery",
          ].map((step, idx) => (
            <div key={idx} className="p-4">
              <div className="text-4xl mb-2">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Applications of Our Data
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto font-raleway">
          {[
            { title: "🤖 Voice Assistants", desc: "Smarter, context-aware Alexa, Siri, Google Assistant." },
            { title: "🏥 Healthcare AI", desc: "Voice biomarkers for disease detection." },
            { title: "🚗 Automotive", desc: "Voice control & sound event detection in vehicles." },
            { title: "📱 Telecom", desc: "Better call routing & customer support AI." },
            { title: "🎮 Gaming & AR/VR", desc: "Immersive soundscapes for next-gen experiences." },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="font-semibold mb-2 text-lg font-orbitron">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight font-orbitron">
          🎧 Ready to Train Your AI with High-Quality Audio?
        </h2>
        <p className="text-lg max-w-xl mx-auto font-raleway">
          Let’s build the future of voice and sound AI together. Reach out to
          get started.
        </p>
      </section>
    </div>
  );
}
